import { useEffect, useRef, useState } from 'react';
import { translate } from '../../lib/translations';
import { MyTeamsModal, useMyTeamsAnchoredModal } from '../MyTeams/MyTeamsModal';

interface SignPlayerModalProps {
  show: boolean;
  onHide: () => void;
  // Retourne une promesse pour permettre au modal d'attendre la fin de l'opération
  onConfirm: (destination: 'nhl' | 'reserve' | 'scratch') => Promise<any>;
  playerName: string;
  language: 'en' | 'fr';
  allowScratch?: boolean;
  allowReserve?: boolean;
  message?: string | null;
  messageSuccess?: boolean;
  onAcknowledge?: () => void;
}

export function SignPlayerModal({ show, onHide, onConfirm, playerName, language, allowScratch = true, allowReserve = true, message = null, messageSuccess = false, onAcknowledge }: SignPlayerModalProps) {
  const anchoredModal = useMyTeamsAnchoredModal();
  const [processingButton, setProcessingButton] = useState<'nhl' | 'reserve' | 'scratch' | null>(null);
  const modalRef = useRef<HTMLDivElement>(null); // 👈 on crée une référence vers le contenu

  useEffect(() => {
    if (show && modalRef.current && !anchoredModal) {
      // 👇 On force le scroll vers l’élément
      modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Le composant n'est jamais démonté entre deux signatures : réinitialiser l'état de traitement à chaque ouverture
    if (show) {
      setProcessingButton(null);
    }
  }, [show, anchoredModal]);

  if (!show) return null;

  const handleButtonClick = async (destination: 'nhl' | 'reserve' | 'scratch') => {
    setProcessingButton(destination);
    try {
      await onConfirm(destination);
      // Ne pas réinitialiser ici : le parent doit fermer le modal ou afficher un message.
    } catch (err) {
      // En cas d'erreur, réactiver les boutons pour permettre une nouvelle tentative
      console.error('SignPlayerModal onConfirm error', err);
      setProcessingButton(null);
    }
  };

  return (
    <MyTeamsModal onClose={onHide} busy={processingButton !== null || !!message}
      className="free-agents-embed" fallbackFocusId="freeagents-page">
      <div 
        className="modal fade show" 
        style={{ 
          display: 'block',
          position: 'absolute',
          top: 0, // pas besoin de scrollY ici
          left: 0,
          width: '100%',
          zIndex: 1050
        }}
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered" ref={modalRef}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {translate('sign.player', language, { playerName })}
              </h5>
              {/* Ne pas afficher la croix si un message est présent pour forcer l'ack */}
              {!message && <button type="button" className="btn-close" aria-label="Fermer" onClick={onHide}></button>}
            </div>
            <div className="modal-body">
              {!message ? (
                <>
                  <p>{translate('where.assign', language, { playerName })}</p>
                  <div className="d-grid gap-2">
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleButtonClick('nhl')}
                      disabled={processingButton !== null}
                    >
                      {processingButton === 'nhl' ? (
                        <span className="d-flex align-items-center justify-content-center">
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {translate('main.club', language)}
                        </span>
                      ) : (
                        translate('main.club', language)
                      )}
                    </button>
                    {allowReserve && (
                      <button 
                        className="btn btn-secondary"
                        onClick={() => handleButtonClick('reserve')}
                        disabled={processingButton !== null}
                      >
                        {processingButton === 'reserve' ? (
                          <span className="d-flex align-items-center justify-content-center">
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {translate('reserves', language)}
                          </span>
                        ) : (
                          translate('reserves', language)
                        )}
                      </button>
                    )}
                    {/* Bouton Scratch : n'afficher que si autorisé par les paramètres de la ligue */}
                    {allowScratch && (
                      <button 
                        className="btn btn-warning"
                        onClick={() => handleButtonClick('scratch')}
                        disabled={processingButton !== null}
                      >
                        {processingButton === 'scratch' ? (
                          <span className="d-flex align-items-center justify-content-center">
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {translate('scratch', language)}
                          </span>
                        ) : (
                          translate('scratch', language)
                        )}
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div role="alert" aria-live="polite" className={`alert ${messageSuccess ? 'alert-success' : 'alert-danger'}`}>
                  {message}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {message ? (
                <div className="d-grid w-100">
                  <button className="btn btn-primary" onClick={onAcknowledge}>
                    {language === 'fr' ? "J'ai compris" : 'OK'}
                  </button>
                </div>
              ) : (
                <>
                <button type="button" className="btn btn-secondary" onClick={onHide}>{language === 'fr' ? 'Annuler' : 'Cancel'}</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div 
        className="modal-backdrop fade show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1040 
        }}
      ></div>
    </MyTeamsModal>
  );
}
